import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useCart } from '../context/CartContext';
import api from '../services/api';
import SEO from '../components/SEO';
import StaticMap from '../components/StaticMap';
import { openPackingSlip } from '../components/PackingSlip';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { formatPrice } from '../utils/format';
import { Button, Skeleton } from '../components/primitives';
import './Account.css';

const AccountOrderDetail = () => {
  const { t, i18n } = useTranslation();
  const siteSettings = useSiteSettings();
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useCustomerAuth();
  const { addToCart } = useCart();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returnReason, setReturnReason] = useState('other');
  const [returnDetail, setReturnDetail] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [returnMessage, setReturnMessage] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: `/account/orders/${id}` } });
    }
  }, [isAuthenticated, authLoading, navigate, id]);

  useEffect(() => {
    if (!token) return;

    const fetchOrder = async () => {
      try {
        const response = await api.get(`/api/customer/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrder(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Order not found');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, token]);

  const handleDownloadInvoice = async () => {
    try {
      const res = await api.get(`/api/customer/orders/${id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${order?.orderNumber || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download invoice');
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? This cannot be undone.')) return;
    try {
      const res = await api.post(`/api/customer/orders/${id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrder(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel order');
    }
  };

  const fmtPrice = (v) => formatPrice(v, { lang: i18n.language });

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    setReturnSubmitting(true);
    setReturnMessage(null);
    try {
      await api.post('/api/customer/returns', {
        orderId: order.id,
        reason: returnReason,
        reasonDetail: returnDetail || null,
        items: (order.items || []).map(item => ({ orderItemId: item.id, quantity: item.quantity })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setReturnMessage({ type: 'success', text: t('account.returnSubmitted', 'Return request submitted successfully.') });
      setShowReturnForm(false);
    } catch (err) {
      setReturnMessage({ type: 'error', text: err.response?.data?.error || t('account.returnFailed', 'Failed to submit return request.') });
    } finally {
      setReturnSubmitting(false);
    }
  };

  const statusSteps = ['pending', 'processing', 'shipped', 'delivered'];

  const statusBadgeClass = (status) => {
    const map = {
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
    };
    return map[status] || 'status-pending';
  };

  const getCurrentStep = (status) => {
    const idx = statusSteps.indexOf(status);
    return idx >= 0 ? idx : 0;
  };

  if (authLoading || loading) {
    return (
      <div className="account-page" aria-busy="true">
        <div className="container">
          <Skeleton.Block height="1.25rem" width="280px" rounded="pill" />
          <Skeleton.Block height="2rem" width="60%" rounded="md" style={{ marginTop: 'var(--space-3)' }} />
          <Skeleton.Block height="160px" rounded="md" style={{ marginTop: 'var(--space-4)' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-page">
        <div className="account-container">
          <div className="account-empty">
            <p>{error}</p>
            <Link to="/account">{t('account.backToAccount', 'Back to Account')}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const currentStep = getCurrentStep(order.status);

  return (
    <div className="account-page">
      <SEO title={`${t('account.order', 'Order')} #${order.orderNumber}`} />

      <div className="account-container">
        <div className="order-detail-header">
          <Link to="/account" className="back-link">
            &larr; {t('account.backToOrders', 'Back to Orders')}
          </Link>
          <div className="order-detail-title">
            <h1>{t('account.order', 'Order')} #{order.orderNumber}</h1>
            <span className={`order-status-badge ${statusBadgeClass(order.status)}`}>
              {t(`order.status.${order.status}`, order.status)}
            </span>
          </div>
          <div className="order-detail-actions">
            <Button
              variant="brand"
              size="md"
              onClick={() => {
                (order.items || []).forEach(item => {
                  addToCart({ id: item.productId, name: item.productName, slug: '', price: item.price, sku: item.productSku, images: [] }, item.quantity);
                });
                navigate('/checkout');
              }}
            >
              {t('account.reorder', 'Reorder')}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => openPackingSlip(order, siteSettings)}
            >
              {t('orderConfirmation.printOrder', 'Print Order')}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleDownloadInvoice}
            >
              {t('account.downloadInvoice', 'Download Invoice')}
            </Button>
            {order.status === 'pending' && (
              <Button
                variant="ghost"
                size="md"
                onClick={handleCancelOrder}
              >
                {t('account.cancelOrder', 'Cancel Order')}
              </Button>
            )}
            {order.status === 'delivered' && siteSettings.returnsEnabled && (
              <Button
                variant="ghost"
                size="md"
                onClick={() => setShowReturnForm(true)}
              >
                {t('account.requestReturn', 'Request Return')}
              </Button>
            )}
          </div>
          <p className="order-detail-date">
            {t('account.placed', 'Placed')} {new Date(order.createdAt).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
            })}
          </p>
        </div>

        {order.status !== 'cancelled' && (
          <div className="order-timeline">
            {statusSteps.map((step, i) => (
              <div key={step} className={`timeline-step ${i <= currentStep ? 'completed' : ''} ${i === currentStep ? 'current' : ''}`}>
                <div className="timeline-dot" />
                {i < statusSteps.length - 1 && <div className="timeline-line" />}
                <span className="timeline-label">{t(`order.status.${step}`, step)}</span>
                {i <= currentStep && (
                  <span className="timeline-date">
                    {step === 'pending' && order.createdAt ? new Date(order.createdAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {step === 'processing' && order.updatedAt && currentStep >= 1 ? new Date(order.updatedAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {step === 'shipped' && order.shippedAt ? new Date(order.shippedAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {step === 'delivered' && order.deliveredAt ? new Date(order.deliveredAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {order.status === 'cancelled' && order.cancellationReason && (
          <div className="order-detail-banner order-detail-banner--error">
            <strong>{t('tracking.cancellationReason', 'Cancellation Reason')}:</strong>{' '}
            <span>{order.cancellationReason}</span>
          </div>
        )}

        {(order.trackingNumber || order.shippingCarrier) && (
          <div className="order-detail-banner">
            {order.shippingCarrier && (
              <div>
                <span className="order-detail-banner__label">{t('tracking.carrier', 'Carrier')}</span>
                <span className="order-detail-banner__value">{order.shippingCarrier}</span>
              </div>
            )}
            {order.trackingNumber && (
              <div>
                <span className="order-detail-banner__label">{t('tracking.trackingNumber', 'Tracking Number')}</span>
                <span className="order-detail-banner__value order-detail-banner__value--mono">{order.trackingNumber}</span>
              </div>
            )}
          </div>
        )}

        <div className="order-detail-grid">
          <div className="order-detail-section">
            <h3>{t('orderConfirmation.items', 'Items')}</h3>
            <div className="order-items-table">
              {order.items?.map(item => (
                <div key={item.id} className="order-detail-item">
                  <div className="item-info">
                    <span className="item-name">{item.productName}</span>
                    <span className="item-sku">{item.productSku}</span>
                  </div>
                  <div className="item-qty">x{item.quantity}</div>
                  <div className="item-price">{fmtPrice(item.subtotal)} {siteSettings.currencyLabel}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="order-detail-sidebar">
            <div className="order-detail-section">
              <h3>{t('orderConfirmation.totals', 'Totals')}</h3>
              <div className="order-detail-totals">
                <div className="detail-total-row">
                  <span>{t('common.subtotal', 'Subtotal')}</span>
                  <span>{fmtPrice(order.subtotal)} {siteSettings.currencyLabel}</span>
                </div>
                <div className="detail-total-row">
                  <span>{t('common.shipping', 'Shipping')}</span>
                  <span>{parseFloat(order.shippingCost) === 0 ? t('common.freeShipping', 'Free') : `${fmtPrice(order.shippingCost)} ${siteSettings.currencyLabel}`}</span>
                </div>
                <div className="detail-total-row" style={siteSettings.taxIncludedInPrice ? { color: '#64748b', fontSize: '0.875rem' } : undefined}>
                  <span>
                    {siteSettings.taxIncludedInPrice
                      ? t('common.taxIncluded', 'Tax (included)')
                      : t('common.tax', 'Tax')}
                  </span>
                  <span>{fmtPrice(order.tax)} {siteSettings.currencyLabel}</span>
                </div>
                <div className="detail-total-row total-final">
                  <span>{t('common.total', 'Total')}</span>
                  <span>{fmtPrice(order.total)} {siteSettings.currencyLabel}</span>
                </div>
                {order.depositAmount && (
                  <div className="order-deposit-card">
                    <div className="order-deposit-row">
                      <span>{t('account.deposit', 'Deposit')}</span>
                      <span>
                        <strong>{fmtPrice(order.depositAmount)} {siteSettings.currencyLabel}</strong>
                        {order.depositPaid && (
                          <span className="order-deposit-paid">
                            ({t('account.paid', 'Paid')})
                          </span>
                        )}
                      </span>
                    </div>
                    {order.balanceDue && (
                      <div className="order-deposit-row">
                        <span>{t('account.balanceDue', 'Balance due')}</span>
                        <strong>{fmtPrice(order.balanceDue)} {siteSettings.currencyLabel}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="order-detail-section">
              <h3>{t('orderConfirmation.deliveryAddress', 'Delivery Address')}</h3>
              <p className="address-text">{order.deliveryAddress?.fullAddress}</p>
              {order.deliveryAddress?.latitude && order.deliveryAddress?.longitude && (
                <div className="order-detail-map">
                  <StaticMap
                    latitude={order.deliveryAddress.latitude}
                    longitude={order.deliveryAddress.longitude}
                    width="100%"
                    height={150}
                  />
                </div>
              )}
            </div>

            {order.notes && (
              <div className="order-detail-section">
                <h3>{t('checkout.notes', 'Notes')}</h3>
                <p className="notes-text">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        {returnMessage && (
          <div
            role="status"
            className={`order-detail-banner ${returnMessage.type === 'success' ? 'order-detail-banner--success' : 'order-detail-banner--error'}`}
          >
            {returnMessage.text}
          </div>
        )}

        {showReturnForm && siteSettings.returnsEnabled && (
          <div
            className="return-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="return-modal-title"
            onClick={() => setShowReturnForm(false)}
          >
            <div className="return-modal" onClick={e => e.stopPropagation()}>
              <h3 id="return-modal-title" className="return-modal__title">
                {t('account.requestReturn', 'Request Return')}
              </h3>
              <form onSubmit={handleReturnSubmit}>
                <div className="return-modal__field">
                  <label className="return-modal__label" htmlFor="return-reason-select">
                    {t('account.returnReason', 'Reason')}
                  </label>
                  <select
                    id="return-reason-select"
                    className="return-modal__select"
                    value={returnReason}
                    onChange={e => setReturnReason(e.target.value)}
                  >
                    <option value="defective">{t('return.reason.defective', 'Defective')}</option>
                    <option value="wrong_item">{t('return.reason.wrong_item', 'Wrong Item')}</option>
                    <option value="not_as_described">{t('return.reason.not_as_described', 'Not as Described')}</option>
                    <option value="changed_mind">{t('return.reason.changed_mind', 'Changed Mind')}</option>
                    <option value="other">{t('return.reason.other', 'Other')}</option>
                  </select>
                </div>
                <div className="return-modal__field">
                  <label className="return-modal__label" htmlFor="return-detail-textarea">
                    {t('account.returnDetail', 'Details (optional)')}
                  </label>
                  <textarea
                    id="return-detail-textarea"
                    className="return-modal__textarea"
                    value={returnDetail}
                    onChange={e => setReturnDetail(e.target.value)}
                    rows={4}
                    placeholder={t('account.returnDetailPlaceholder', 'Please describe why you want to return this order...')}
                  />
                </div>
                <div className="return-modal__actions">
                  <Button type="submit" variant="brand" size="md" disabled={returnSubmitting}>
                    {returnSubmitting ? t('common.submitting', 'Submitting...') : t('account.submitReturn', 'Submit Return Request')}
                  </Button>
                  <Button variant="secondary" size="md" onClick={() => setShowReturnForm(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountOrderDetail;
